import { request } from './client.js';
import { ApiError, DuplicateTicketApiError } from './errors.js';
/**
 * createTicket — POST /valet/tickets
 *
 * Headers injected automatically by the client:
 *   - Authorization: Bearer <token>
 *   - X-Location-Id: <active location id from localStorage>
 *
 * On 409 duplicate_active_ticket, throws DuplicateTicketApiError with existingTicketNumber.
 */
export async function createTicket(input, options) {
    try {
        return await request('/valet/tickets', {
            method: 'POST',
            body: input,
            headers: options?.headers,
            suppressLocationAccessHandling: options?.suppressLocationAccessHandling,
            signal: options?.signal,
        });
    }
    catch (err) {
        // Re-throw as typed DuplicateTicketApiError for PWA duplicate-plate dialog
        if (err instanceof Error &&
            'code' in err &&
            err.code === 'duplicate_active_ticket' &&
            'status' in err &&
            err.status === 409) {
            const details = 'details' in err ? err.details : undefined;
            const existingTicketNumber = details?.existingTicketNumber ?? 0;
            throw new DuplicateTicketApiError(existingTicketNumber);
        }
        throw err;
    }
}
/**
 * getCheckinFormConfig — GET /valet/locations/:locationId/checkin-form-config
 *
 * Returns aggregate form config: niche, checkin required fields, ticket types,
 * and tariff snapshot (nullable).
 */
export async function getCheckinFormConfig(locationId) {
    return request(`/valet/locations/${encodeURIComponent(locationId)}/checkin-form-config`, { method: 'GET' });
}
/**
 * requestPhotoUploadUrls — POST /valet/tickets/photo-upload-urls
 *
 * Issues 4 presigned PUT URLs (one per angle) for direct browser-to-bucket upload.
 * The X-Location-Id header is auto-injected by the client (path not in SKIP list).
 * Caller must be an operator with the target location active in localStorage.
 */
export async function requestPhotoUploadUrls(options) {
    return request('/valet/tickets/photo-upload-urls', {
        method: 'POST',
        body: {},
        headers: options?.headers,
        suppressLocationAccessHandling: options?.suppressLocationAccessHandling,
        signal: options?.signal,
    });
}
/**
 * requestIncidentPhotoUploadUrls — POST /valet/tickets/incident-photo-upload-urls
 *
 * Issues `count` (1..20) presigned PUT URLs (angle-less) for optional incident
 * (pre-existing damage) photos. The X-Location-Id header is auto-injected by
 * the client. Parallel to requestPhotoUploadUrls — independent of the
 * mandatory 4-angle flow.
 */
export async function requestIncidentPhotoUploadUrls(count, options) {
    return request('/valet/tickets/incident-photo-upload-urls', {
        method: 'POST',
        body: { count },
        headers: options?.headers,
        suppressLocationAccessHandling: options?.suppressLocationAccessHandling,
        signal: options?.signal,
    });
}
/**
 * getRetrievalQueue — GET /valet/tickets/retrieval-queue
 *
 * Returns the FIFO list of requested tickets at the valet's location.
 * X-Location-Id is auto-injected by the api-client (JQ4-25, REQ-02).
 */
export async function getRetrievalQueue() {
    return request('/valet/tickets/retrieval-queue', { method: 'GET' });
}
/**
 * getMyActiveTickets — GET /valet/tickets/my-active
 *
 * Returns the calling valet's in_transit tickets at their location.
 * X-Location-Id is auto-injected by the api-client (JQ4-25, REQ-05).
 */
export async function getMyActiveTickets() {
    return request('/valet/tickets/my-active', { method: 'GET' });
}
/**
 * claimTicket — POST /valet/tickets/:id/claim
 *
 * Claims a requested ticket (requested → in_transit) atomically.
 * Throws ApiError(409, 'ticket_already_claimed') when another valet won the race.
 * Throws ApiError(404, 'ticket_not_found') when the ticket does not exist.
 * (JQ4-25, REQ-03/04).
 */
export async function claimTicket(ticketId) {
    return request(`/valet/tickets/${encodeURIComponent(ticketId)}/claim`, {
        method: 'POST',
        body: {},
    });
}
/**
 * deliverTicket — POST /valet/tickets/:id/deliver
 *
 * Delivers an in_transit ticket (in_transit → delivered) atomically.
 * Throws ApiError(409, 'ticket_already_delivered') when the ticket is not in_transit.
 * Throws ApiError(404, 'ticket_not_found') when the ticket does not exist.
 * (JQ4-26, US-TICKETS-006).
 */
export async function deliverTicket(ticketId) {
    return request(`/valet/tickets/${encodeURIComponent(ticketId)}/deliver`, {
        method: 'POST',
        body: {},
    });
}
/**
 * markTicketReady — PATCH /valet/tickets/:id/ready
 *
 * Marks an in_transit ticket ready for pickup (post-payment-delay-recharge S1).
 * Idempotent — repeat calls return the SAME anchor (readyForPickupAt does not move).
 * Throws ApiError(409, 'ticket_already_delivered') when the ticket is delivered/cancelled.
 * Throws ApiError(404, 'ticket_not_found') when the ticket does not exist.
 */
export async function markTicketReady(ticketId) {
    return request(`/valet/tickets/${encodeURIComponent(ticketId)}/ready`, {
        method: 'PATCH',
        body: {},
    });
}
/**
 * searchTickets — GET /valet/tickets/search?q=<query>
 *
 * Returns active tickets at the valet's location matching the query by
 * plate, firstName, lastName, phone, or ticketNumber.
 * X-Location-Id is auto-injected by the api-client (JQ4-23, US-TICKETS-003).
 */
export async function searchTickets(q) {
    return request(`/valet/tickets/search?q=${encodeURIComponent(q)}`, {
        method: 'GET',
    });
}
/**
 * getTicketAmountDue — GET /valet/tickets/:id/amount-due
 *
 * Server-computed charge (same calculator as the cash-payment endpoint) plus
 * the location currency, so the cash dialog can show what is owed.
 */
export async function getTicketAmountDue(ticketId) {
    return request(`/valet/tickets/${encodeURIComponent(ticketId)}/amount-due`, { method: 'GET' });
}
/**
 * registerCashPayment — POST /valet/tickets/:id/cash-payment
 *
 * Registers a cash payment for an unpaid ticket.
 * Returns updated ticket fields (paymentStatus, paymentMethod, paidAt).
 * Throws ApiError(409) if already paid; ApiError(422) if ticket is free-tariff ($0);
 * ApiError(400) if amountCents is invalid.
 * (JQ4-38, US-PAGOS-004, WU-4).
 */
export async function registerCashPayment(ticketId, amountCents) {
    return request(`/valet/tickets/${encodeURIComponent(ticketId)}/cash-payment`, {
        method: 'POST',
        body: { amountCents },
    });
}
/**
 * applyComp — POST /valet/tickets/:id/comp
 *
 * Marks an unpaid ticket as a courtesy (comp). Requires a non-empty reason.
 * Returns updated ticket fields (paymentMethod='comp', paymentStatus='paid').
 * Throws ApiError(403, 'comps_disabled') when comps are not enabled for the location.
 * Throws ApiError(403, 'comp_not_authorized') when the actor's role is not authorized.
 * Throws ApiError(409) if the ticket is already paid.
 * (JQ4-19, US-CONFIG-005, slice 3).
 */
export async function applyComp(ticketId, reason) {
    return request(`/valet/tickets/${encodeURIComponent(ticketId)}/comp`, {
        method: 'POST',
        body: { reason },
    });
}
/**
 * getValetTicketPhotos — GET /valet/tickets/:id/photos
 *
 * ticket-photos-for-valet — least-privilege photo listing (photos +
 * incidentPhotos ONLY, no billing/audit fields) so a valet can validate the
 * vehicle's condition at delivery. Signed URLs are short-TTL (5 min,
 * backend-enforced) — callers should re-request rather than cache across a
 * long-idle dialog session.
 * Throws ApiError(404, 'ticket_not_found') when the ticket does not exist,
 * belongs to another org, or belongs to a different location than the
 * caller's active one (no cross-location existence leak).
 */
export async function getValetTicketPhotos(ticketId) {
    return request(`/valet/tickets/${encodeURIComponent(ticketId)}/photos`, { method: 'GET' });
}
/**
 * putPhoto — PUT to a presigned S3 URL (direct bucket upload).
 *
 * CRITICAL: Does NOT use the api-client `request()` helper —
 * presigned URLs are signed to a specific Content-Type and MUST NOT
 * include Authorization headers (they are bucket URLs, not API URLs).
 * Throws ApiError on non-OK response.
 */
export async function putPhoto(url, blob, signal) {
    let res;
    try {
        res = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'image/jpeg' },
            body: blob,
            signal,
        });
    }
    catch (err) {
        throw new ApiError('network_error', err instanceof Error ? err.message : 'fetch failed during photo upload');
    }
    if (!res.ok) {
        // S3-compatible stores reply with an XML body whose <Code> names the real
        // cause (SignatureDoesNotMatch, AccessDenied, ExpiredToken...). Keep it for
        // diagnosis only: the technical text must not reach the operator UI, which
        // maps `photo_upload_failed` to a translated message.
        let s3Code;
        try {
            const text = await res.text();
            s3Code = /<Code>([^<]+)<\/Code>/.exec(text)?.[1];
        }
        catch {
            // unreadable body — status alone is still reported
        }
        console.warn(`[putPhoto] upload failed: HTTP ${res.status}${s3Code ? ` ${s3Code}` : ''}`);
        throw new ApiError('photo_upload_failed', `Photo upload failed: HTTP ${res.status}`, res.status, { s3Code });
    }
}
//# sourceMappingURL=tickets.js.map